import React from 'react';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RepeatSharp from '@mui/icons-material/RepeatSharp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CancelOutlined } from '@mui/icons-material';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { Theme } from '@mui/material/styles';
import {
  LoanType,
  PropertyStatus,
  CoBorrowerType,
  LoanPurpose,
  PropertyAvailable,
} from '@/config/enums';

export interface Option {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

/**
 * Configuration for Loan Type options
 * Icon sizing follows the pattern: CardInfo icons = 24x24px (within 32x32px container)
 */
export const loanTypeOptions = (
  t: (key: string) => string,
  theme: Theme
): Option[] => {
  const primaryColor = theme.palette.M3?.sys?.light?.primary || '#2CCCD3';

  return [
    {
      id: LoanPurpose.Purchase,
      icon: React.createElement(HomeOutlinedIcon, {
        sx: {
          width: '24px',
          height: '24px',
          color: primaryColor,
        },
      }),
      title: t('loanSetup.loanType.options.purchase.title'),
      description: t('loanSetup.loanType.options.purchase.description'),
    },
    {
      id: LoanPurpose.RefinanceCashOut,
      icon: React.createElement(RepeatSharp, {
        sx: {
          width: '24px',
          height: '24px',
          color: primaryColor,
        },
      }),
      title: t('loanSetup.loanType.options.refinance.title'),
      description: t('loanSetup.loanType.options.refinance.description'),
    },
  ];
};

/**
 * Configuration for Property Status options
 * Icon sizing follows the pattern: CardInfo icons = 24x24px (within 32x32px container)
 */
export const propertyStatusOptions = (
  t: (key: string) => string,
  theme: Theme
): Option[] => {
  const primaryColor = theme.palette.M3?.sys?.light?.primary || '#2CCCD3';

  return [
    {
      id: PropertyAvailable.SELECTED,
      icon: React.createElement(CheckCircleOutlineIcon, {
        sx: {
          width: '24px',
          height: '24px',
          color: primaryColor,
        },
      }),
      title: t('loanSetup.propertyStatus.options.yes.title'),
      description: t('loanSetup.propertyStatus.options.yes.description'),
    },
    {
      id: PropertyAvailable.LOOKING,
      icon: React.createElement(CancelOutlined, {
        sx: {
          width: '24px',
          height: '24px',
          color: primaryColor,
        },
      }),
      title: t('loanSetup.propertyStatus.options.no.title'),
      description: t('loanSetup.propertyStatus.options.no.description'),
    },
  ];
};

/**
 * Configuration for Co-Borrower options
 * Icon sizing follows the pattern: CardInfo icons = 24x24px (within 32x32px container)
 */
export const coBorrowerOptions = (
  t: (key: string) => string,
  theme: Theme
): Option[] => {
  const primaryColor = theme.palette.M3?.sys?.light?.primary || '#2CCCD3';

  return [
    {
      id: CoBorrowerType.INDIVIDUAL,
      icon: React.createElement(PermIdentityOutlinedIcon, {
        sx: {
          width: '24px',
          height: '24px',
          color: primaryColor,
        },
      }),
      title: t('loanSetup.coBorrower.options.individual.title'),
      description: t('loanSetup.coBorrower.options.individual.description'),
    },
    {
      id: CoBorrowerType.JOINT,
      icon: React.createElement(PeopleAltOutlinedIcon, {
        sx: {
          width: '24px',
          height: '24px',
          color: primaryColor,
        },
      }),
      title: t('loanSetup.coBorrower.options.joint.title'),
      description: t('loanSetup.coBorrower.options.joint.description'),
    },
  ];
};
